import { AdvEmpty, AdvHeading, AdvListing, AdvSection, AdvUrgent, Btn, DirectoryFilters, IconCard, SpecSheet, useText } from '@kkf/design'
import {
  GARAGE_BAND,
  GARAGE_ENTERPRISE,
  GARAGE_SERVICES,
  GARAGE_SERVICES_HEADING,
  GARAGE_SPECS,
} from '../data/garage'
import { useListingFilter } from '../components/useListingFilter'
import { useVocabulary } from '../hooks/useVocabulary'
import { useT } from '../i18n'

/**
 * The paid side of the workshop, searchable and filtered by group.
 *
 * Same shape as `/tours` and `/programmes` deliberately: one register across the three
 * listings, so a visitor moving between them is reading the same page rather than three
 * inherited templates. The differences are the filter's axis and what the page ends on.
 *
 * This one ends on the recovery number, because the visitor most likely to be on it is
 * the one whose day has gone wrong — and it is the one page besides the home page that
 * carries `AdvUrgent`.
 */
export function ServicesPage() {
  const t = useT()
  const word = useVocabulary()
  const s = useText()
  const f = useListingFilter({
    items: GARAGE_SERVICES,
    groupId: 'group',
    groupTitle: t('services.group'),
    groupOf: (service) => service.group,
    labelOf: word,
    // BOTH languages, not the reader's: somebody who knows a service by its English name
    // should still find it in a Mongolian page, and indexing one language would make the
    // memoised index depend on the locale.
    searchText: (service) =>
      `${service.title.en} ${service.title.mn} ${service.group} ${service.descr.en} ${service.descr.mn}`,
    order: ['Recovery', 'Workshop', 'Together'],
  })

  return (
    <>
      <AdvSection glow="tl" className="ul-adv-page-top" containerClassName="wow animate__fadeInUp">
        {/* This page's own name — the one <h1> on it. The garage's inner pages carry no
            breadcrumb banner, so the opening heading is the page heading. */}
        <AdvHeading
          eyebrow={s(GARAGE_SERVICES_HEADING.subTitle)}
          title={t('band.recovery')}
          titleAccent={t('band.recoveryAccent')}
          lead={s(GARAGE_SERVICES_HEADING.descr)}
          as="h1"
        />
      </AdvSection>

      <AdvSection glow="tr" flush containerClassName="wow animate__fadeInUp">
        <AdvListing
          rail={
            <DirectoryFilters
              search={f.search}
              onSearchChange={f.setSearch}
              searchPlaceholder={t('services.search')}
              groups={f.groups}
              selected={f.selected}
              onToggle={f.toggle}
              onClear={f.clear}
              resultSummary={f.summary}
            />
          }
        >
          {f.results.length === 0 ? (
            <AdvEmpty onClear={f.clear}>
              Nothing on the service list matches that. If it is not here, call the number
              at the bottom of this page and ask — the list is what we do most, not
              everything we can do.
            </AdvEmpty>
          ) : (
            <div className="row row-cols-lg-2 row-cols-1 ul-bs-row">
              {f.results.map((service) => (
                <div className="col" key={s(service.title)}>
                  {/* Image-topped rather than icon-led. The icon font is
                      `flaticon_charitics` and has no wrench, no spanner and no vehicle
                      in it — the closest it got to a workshop was a parcel. A photograph
                      of the actual bench says more than a mismatched glyph. */}
                  <IconCard
                    item={{
                      title: s(service.title),
                      descr: s(service.descr),
                      image: service.image,
                      imageAlt: s(service.imageAlt),
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </AdvListing>
      </AdvSection>

      {/* WHAT WE CAN DO ON SITE — the facts, as a sheet because they are sentences. */}
      <AdvSection glow="bl" containerClassName="wow animate__fadeInUp">
        <AdvHeading eyebrow={t('home.workshopEyebrow')} title={t('home.canDo')} titleAccent={t('home.canDoAccent')} />
        <SpecSheet
          rows={GARAGE_SPECS.map((spec) => ({ label: s(spec.term), values: [s(spec.detail)] }))}
        />
      </AdvSection>

      {/* THE SOCIAL ENTERPRISE — why the paid side exists. */}
      <AdvSection glow="band" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          eyebrow={s(GARAGE_ENTERPRISE.subTitle)}
          title={t('services.located')}
          titleAccent={t('services.locatedAccent')}
        />
        {GARAGE_ENTERPRISE.paragraphs.map((p) => (
          <p className="ul-adv-lead" key={s(p)}>
            {s(p)}
          </p>
        ))}
        <div className="ul-adv-hero-btns">
          <Btn to={GARAGE_ENTERPRISE.ctaTo} className="ul-btn--ghost">
            {s(GARAGE_ENTERPRISE.ctaLabel)}
          </Btn>
        </div>
      </AdvSection>

      {/* OFF THE ROAD? — the signal glow, once per page. */}
      <AdvSection glow="signal" tight edged containerClassName="wow animate__fadeInUp">
        <AdvUrgent
          eyebrow={s(GARAGE_BAND.subTitle)}
          title={t('services.call')}
          titleAccent={t('services.workshop')}
          note={s(GARAGE_BAND.note)}
        >
          <Btn to={GARAGE_BAND.ctaTo} className="ul-btn--signal">
            {s(GARAGE_BAND.ctaLabel)}
          </Btn>
        </AdvUrgent>
      </AdvSection>
    </>
  )
}
